import React, { useState, useEffect } from 'react';
import { Tabs, Card, DatePicker, Select, Button, Row, Col, Statistic, Modal } from 'antd';
import {
    BarChartOutlined,
    LineChartOutlined,
    PieChartOutlined,
    FundOutlined,
    SaveOutlined,
    PlusOutlined,
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import {
    fetchDealInsights,
    fetchDemographics,
    fetchPerformance,
    fetchCampaignAnalytics,
    fetchSavedAnalytics,
} from '@/redux/rtk/features/CRM/analytics/analyticsSlice';
import { useTranslation } from "react-i18next";
import DealInsights from './PrebuiltAnalytics/DealInsights';
import Demographics from './PrebuiltAnalytics/Demographics';
import PerformanceMetrics from './PrebuiltAnalytics/PerformanceMetrics';
import CampaignAnalytics from './PrebuiltAnalytics/CampaignAnalytics';
import MyAnalyticsDashboard from './SavedAnalytics/MyAnalyticsDashboard';
import dayjs from 'dayjs';
import DrillDownTable from '../../CRM/Dashboard/DrillDownTable';

const { RangePicker } = DatePicker;
const { Option } = Select;

const AnalyticsDashboard = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [activeTab, setActiveTab] = useState('deal-insights');
    const [dateRange, setDateRange] = useState([
        dayjs().subtract(3, 'months'),
        dayjs(),
    ]);
    const [filters, setFilters] = useState({});

    // Drill down state
    const [drillDown, setDrillDown] = useState({
        visible: false,
        title: "",
        type: "leads",
        filters: {},
        data: null
    });

    const handleDrillDown = (title, type, drillFilters, data = null) => {
        setDrillDown({
            visible: true,
            title,
            type,
            filters: {
                ...drillFilters,
                startDate: dateRange[0]?.format('YYYY-MM-DD'),
                endDate: dateRange[1]?.format('YYYY-MM-DD'),
            },
            data
        });
    };

    const { dealInsights, loadingDealInsights } = useSelector(
        (state) => state.analytics
    );
    const startDate = dateRange?.[0]?.format('YYYY-MM-DD');
    const endDate = dateRange?.[1]?.format('YYYY-MM-DD');
    const filtersStr = JSON.stringify(filters);

    console.log('AnalyticsDashboard Render');
    useEffect(() => {
        console.log('AnalyticsDashboard useEffect triggered. Dependencies:', {
            activeTab,
            startDate,
            endDate,
            filtersStr
        });

        // Load data based on active tab
        loadTabData(activeTab);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, startDate, endDate, filtersStr]);

    const loadTabData = (tab) => {
        const filterParams = {
            startDate: dateRange[0]?.format('YYYY-MM-DD'),
            endDate: dateRange[1]?.format('YYYY-MM-DD'),
            ...filters,
        };

        switch (tab) {
            case 'deal-insights':
                dispatch(fetchDealInsights(filterParams));
                break;
            case 'demographics':
                dispatch(fetchDemographics({ type: 'all', filters: filterParams }));
                break;
            case 'performance':
                dispatch(fetchPerformance({ type: 'all', filters: filterParams }));
                break;
            case 'campaign':
                dispatch(fetchCampaignAnalytics(filterParams));
                break;
            case 'my-analytics':
                dispatch(fetchSavedAnalytics({ includePublic: true }));
                break;
            default:
                break;
        }
    };

    const handleDateRangeChange = (dates) => {
        setDateRange(dates || [dayjs().subtract(3, 'months'), dayjs()]);
    };

    const handleTabChange = (key) => {
        setActiveTab(key);
    };

    return (
        <div className="analytics-dashboard" style={{ padding: '24px' }}>
            {/* Header */}
            <div style={{ marginBottom: '24px' }}>
                <Row justify="space-between" align="middle">
                    <Col>
                        <h1 style={{ margin: 0, fontSize: '28px', fontWeight: 'bold' }}>
                            <BarChartOutlined style={{ marginRight: '12px', marginLeft: '12px' }} />
                            {t('analytics.title')}
                        </h1>
                        <p style={{ color: '#666', marginTop: '8px' }}>
                            {t('analytics.subTitle')}
                        </p>
                    </Col>
                </Row>
            </div>

            {/* Filters Bar */}
            <Card style={{ marginBottom: '24px' }}>
                <Row gutter={16} align="middle">
                    <Col>
                        <span style={{ marginRight: '8px', fontWeight: '500' }}>
                            {t('analytics.dateRange')}:
                        </span>
                    </Col>
                    <Col>
                        <RangePicker
                            value={dateRange}
                            onChange={handleDateRangeChange}
                            format="YYYY-MM-DD"
                            style={{ width: '280px' }}
                            presets={[
                                { label: t('common.lastWeek'), value: [dayjs().subtract(1, 'week'), dayjs()] },
                                { label: t('common.lastMonth'), value: [dayjs().subtract(1, 'month'), dayjs()] },
                                { label: t('common.last3Months'), value: [dayjs().subtract(3, 'months'), dayjs()] },
                                { label: t('analytics.last6Months'), value: [dayjs().subtract(6, 'months'), dayjs()] },
                                { label: t('analytics.lastYear'), value: [dayjs().subtract(1, 'year'), dayjs()] },
                            ]}
                        />
                    </Col>
                    <Col flex="auto" />
                    <Col>
                        <Button
                            type="primary"
                            icon={<SaveOutlined />}
                            onClick={() => loadTabData(activeTab)}
                        >
                            {t('common.refresh')}
                        </Button>
                    </Col>
                </Row>
            </Card>

            {/* Summary Stats - Quick Overview */}
            {activeTab === 'deal-insights' && dealInsights && (
                <Row gutter={16} style={{ marginBottom: '24px' }}>
                    <Col span={6}>
                        <Card>
                            <Statistic
                                title={t('dashboard.totalDeals')}
                                value={dealInsights.totalDeals || 0}
                                prefix={<FundOutlined />}
                                valueStyle={{ color: '#3f8600' }}
                            />
                        </Card>
                    </Col>
                    <Col span={6}>
                        <Card>
                            <Statistic
                                title={t('analytics.scholarship')}
                                value={dealInsights.totalScholarshipAmount || 0}
                                prefix="$"
                                precision={0}
                                valueStyle={{ color: '#1890ff' }}
                            />
                        </Card>
                    </Col>
                    <Col span={6}>
                        <Card>
                            <Statistic
                                title={t('analytics.wonDeals')}
                                value={
                                    dealInsights.dealsWonLost?.find((d) => d.status === 'Won')
                                        ?.count || 0
                                }
                                valueStyle={{ color: '#52c41a' }}
                            />
                        </Card>
                    </Col>
                    <Col span={6}>
                        <Card>
                            <Statistic
                                title={t('analytics.lostDeals')}
                                value={
                                    dealInsights.dealsWonLost?.find((d) => d.status === 'Lost')
                                        ?.count || 0
                                }
                                valueStyle={{ color: '#ff4d4f' }}
                            />
                        </Card>
                    </Col>
                </Row>
            )}

            {/* Main Tabs */}
            <Tabs
                activeKey={activeTab}
                onChange={handleTabChange}
                type="card"
                size="large"
                items={[
                    {
                        key: 'deal-insights',
                        label: (
                            <span>
                                <BarChartOutlined style={{ marginInlineEnd: '8px' }} />
                                {t('analytics.dealInsights')}
                            </span>
                        ),
                        children: <DealInsights onDrillDown={handleDrillDown} />,
                    },
                    {
                        key: 'demographics',
                        label: (
                            <span>
                                <PieChartOutlined style={{ marginInlineEnd: '8px' }} />
                                {t('analytics.demographics')}
                            </span>
                        ),
                        children: <Demographics onDrillDown={handleDrillDown} />,
                    },
                    {
                        key: 'performance',
                        label: (
                            <span>
                                <LineChartOutlined style={{ marginInlineEnd: '8px' }} />
                                {t('analytics.performance')}
                            </span>
                        ),
                        children: <PerformanceMetrics onDrillDown={handleDrillDown} />,
                    },
                    {
                        key: 'campaign',
                        label: (
                            <span>
                                <FundOutlined style={{ marginInlineEnd: '8px' }} />
                                {t('analytics.campaign')}
                            </span>
                        ),
                        children: <CampaignAnalytics onDrillDown={handleDrillDown} />,
                    },
                    {
                        key: 'my-analytics',
                        label: (
                            <span>
                                <SaveOutlined style={{ marginInlineEnd: '8px' }} />
                                {t('analytics.myAnalytics')}
                            </span>
                        ),
                        children: <MyAnalyticsDashboard onDrillDown={handleDrillDown} />,
                    },
                ]}
            />

            <Modal
                title={drillDown.title}
                open={drillDown.visible}
                onCancel={() => setDrillDown(prev => ({ ...prev, visible: false }))}
                footer={null}
                width={1000}
                destroyOnClose
            >
                <DrillDownTable type={drillDown.type} filters={drillDown.filters} data={drillDown.data} />
            </Modal>
        </div>
    );
};

export default AnalyticsDashboard;
